import { Dialog, DialogActions, DialogContent } from '@mui/material'
import AppDialogTitle from '../../../../ui/appDialogTitle'
import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  TextField,
  IconButton,
} from '@mui/material';
import { Add, Clear, ContentCopy } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { Colors } from '../../../../../styles/theme';
import ContactLensSelectDialog from '../../../../admin/inventory/contacts/contactLensSelectDialog';
import { formatAmountForDisplay, sumMoney } from '../../../../../utils';
import { ButtonType } from './invoiceButtonComponents';
import SelectRxDialog from '../../rx/selectRxDialog';
import RxSection from './RxSection';
import { v4 as uuidv4 } from 'uuid'
import TreatmentSelectDialog from '../../../../admin/ValidData/validTreatment/treatmentSelectDialog';
import { RxService } from '../../../../../service/rxService';

function NewContactsDialog({ onClose, open, onSelect, customer }) {

  const initialEyeData = {
    id: null,
    supplier: { supplierName: '' },
    brand: { name: '' },
    properties: {
      lensType: '',
      material: '',
      baseCurve: '',
      diameter: '',
      power: '',
      cylinder: '',
      axis: '',
      addPower: ''
    },
    listPrice: 0.0,
    modelNumber: '',
    rx: null
  };

  const [contactLensData, setContactLensData] = useState({
    od: { ...initialEyeData },
    os: { ...initialEyeData }
  });

  const [contactLensSelectDialogOpen, setContactLensSelectDialogOpen] = useState({ od: false, os: false });
  const [rxSelectDialogOpen, setRxSelectDialogOpen] = useState(false);
  const [selectedRx, setSelectedRx] = useState(null);

  // Reset data on open
  useEffect(() => {
    if (open) {
      setContactLensData({
        od: { ...initialEyeData },
        os: { ...initialEyeData }
      });
      setSelectedRx(null);
    }
  }, [open]);

  const handleContactLensSelect = (selectedLens, eye) => {
    setContactLensData(prev => ({ ...prev, [eye]: { ...selectedLens, rx: selectedRx } }));
    setContactLensSelectDialogOpen(prev => ({ ...prev, [eye]: false }));
  }

  const handleClearEye = (eye) => {
    setContactLensData(prev => ({ ...prev, [eye]: { ...initialEyeData } }));
  };

  const handleCopyEye = (sourceEye, targetEye) => {
    setContactLensData(prev => ({ ...prev, [targetEye]: { ...prev[sourceEye] } }));
  };

  const handleInlineRxSave = async (editedRx) => {
    try {
      const response = await RxService.update(editedRx);
      const updatedRx = response?.data || editedRx;
      setSelectedRx(updatedRx);
      toast.success('Rx updated successfully');
      return updatedRx;
    } catch (error) {
      toast.error('Failed to update rx. Please try again.');
      throw error;
    }
  };

  const totals = useMemo(() => {
    return sumMoney([contactLensData.od.listPrice || 0, contactLensData.os.listPrice || 0]);
  }, [contactLensData.od, contactLensData.os])

  const handleSave = () => {
    const groupId = uuidv4();
    const dataToSave = {
      name: 'Contacts',
      type: ButtonType.NEW_CONTACTS,
      contactLens: contactLensData, // Store OD and OS data
      total: totals,
      groupId: groupId,
    }

    onSelect(dataToSave)
  }

  return (
    <Dialog onClose={onClose} open={open} fullScreen maxWidth="lg" fullWidth sx={{ bgcolor: '#f0f0f0' }} >
      <AppDialogTitle onClose={onClose} title="New Contacts Order" />
      <DialogContent>
        <Grid container mt={1} spacing={1}>
          <Grid item md={12} lg={6} p={1}>
            <Grid container spacing={1} p={1} boxShadow={2} sx={{ mb: 2, border: '1px solid #ccc', borderRadius: 1 }}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', p: 1, backgroundColor: Colors.secondary }}>
                  <Typography sx={{ flexGrow: 1 }}>Right Eye (OD) Contact Lens</Typography>
                  <IconButton size="small" onClick={() => handleClearEye('od')} title="Clear OD">
                    <Clear />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleCopyEye('od', 'os')} title="Copy OD to OS">
                    <ContentCopy />
                  </IconButton>
                  <Button size='small' variant='contained' onClick={() => setContactLensSelectDialogOpen(prev => ({ ...prev, od: true }))}>Add Contact Lens</Button>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Model #"
                  variant="outlined"
                  size="small"
                  value={contactLensData.od.modelNumber || ''}
                  disabled
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Supplier"
                  variant="outlined"
                  size="small"
                  disabled
                  value={contactLensData.od.supplier?.supplierName || ''}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Brand"
                  variant="outlined"
                  size="small"
                  disabled
                  value={contactLensData.od.brand?.name || ''}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Lens Type"
                  variant="outlined"
                  size="small"
                  disabled
                  value={contactLensData.od.properties?.lensType || ''}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Material"
                  fullWidth
                  variant="outlined"
                  size="small"
                  disabled
                  value={contactLensData.od.properties?.material || ''}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  variant="filled"
                  color='success'
                  size="small"
                  label="Price"
                  disabled
                  value={formatAmountForDisplay(contactLensData.od.listPrice || 0)}
                />
              </Grid>
            </Grid>

            <Grid container spacing={1} p={1} boxShadow={2} sx={{ mb: 2, border: '1px solid #ccc', borderRadius: 1 }}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', p: 1, backgroundColor: Colors.secondary }}>
                  <Typography sx={{ flexGrow: 1 }}>Left Eye (OS) Contact Lens</Typography>
                  <IconButton size="small" onClick={() => handleClearEye('os')} title="Clear OS">
                    <Clear />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleCopyEye('os', 'od')} title="Copy OS to OD">
                    <ContentCopy />
                  </IconButton>
                  <Button size='small' variant='contained' onClick={() => setContactLensSelectDialogOpen(prev => ({ ...prev, os: true }))}>Add Contact Lens</Button>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Model #"
                  variant="outlined"
                  size="small"
                  value={contactLensData.os.modelNumber || ''}
                  disabled
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Supplier"
                  variant="outlined"
                  size="small"
                  disabled
                  value={contactLensData.os.supplier?.supplierName || ''}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Brand"
                  variant="outlined"
                  size="small"
                  disabled
                  value={contactLensData.os.brand?.name || ''}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Lens Type"
                  variant="outlined"
                  size="small"
                  disabled
                  value={contactLensData.os.properties?.lensType || ''}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Material"
                  fullWidth
                  variant="outlined"
                  size="small"
                  disabled
                  value={contactLensData.os.properties?.material || ''}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  variant="filled"
                  color='success'
                  size="small"
                  label="Price"
                  disabled
                  value={formatAmountForDisplay(contactLensData.os.listPrice || 0)}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item md={12} lg={6} p={1}>
            <RxSection
              selectedRx={selectedRx}
              setRxSelectDialogOpen={setRxSelectDialogOpen}
              onSaveRx={handleInlineRxSave}
              rxType='contacts'
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Total Price</Typography>
              <TextField
                variant="outlined"
                value={formatAmountForDisplay(totals)}
                sx={{ width: '100px', bgcolor: 'black', '& .MuiInputBase-input': { color: 'white' } }}
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 1, boxShadow: 8 }}>
        <Button onClick={handleSave} variant='contained'>
          Add to invoice
        </Button>
        <Button color="cancel" variant="outlined" onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
      <ContactLensSelectDialog
        open={contactLensSelectDialogOpen.od}
        onClose={() => setContactLensSelectDialogOpen(prev => ({ ...prev, od: false }))}
        onSelect={(lens) => handleContactLensSelect(lens, 'od')}
      />
       <ContactLensSelectDialog
        open={contactLensSelectDialogOpen.os}
        onClose={() => setContactLensSelectDialogOpen(prev => ({ ...prev, os: false }))}
        onSelect={(lens) => handleContactLensSelect(lens, 'os')}
      />
      <SelectRxDialog
        open={rxSelectDialogOpen}
        onClose={() => setRxSelectDialogOpen(false)}
        onSelect={setSelectedRx}
        customerId={customer?.customer?.id}
      />

    </Dialog>
  )
}

export default NewContactsDialog